import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Check, Loader2, Upload } from "lucide-react";
import * as adminApi from "@/api/admin";
import RichTextEditor from "@/components/RichTextEditor";
import { ImageDropzone } from "./ImageDropzone";

// Slug shared by the PageContent (heading) and PageImage (photo) rows
// backing the Nova "Pizza Fan" featured-offer section.
const SLUG = "nova-featured-offer";

function htmlToPlainText(html: string) {
  if (typeof window === "undefined") return html.replace(/<[^>]+>/g, " ");
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.replace(/\s+/g, " ").trim() || "";
}

export function NovaFeaturedOfferView() {
  const [heading, setHeading] = useState("");
  const [headingHtml, setHeadingHtml] = useState("");
  const [origHeadingHtml, setOrigHeadingHtml] = useState("");
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingText, setSavingText] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.getPageContent(SLUG),
      adminApi.getPageImage(SLUG),
    ])
      .then(([content, imageUrl]) => {
        const nextHeadingHtml = content.content || content.title || "";
        setHeading(content.title || htmlToPlainText(nextHeadingHtml));
        setHeadingHtml(nextHeadingHtml);
        setOrigHeadingHtml(nextHeadingHtml);
        setCurrentImageUrl(imageUrl);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSaveText = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSavingText(true);
    try {
      await adminApi.updatePageContent(SLUG, {
        title: htmlToPlainText(headingHtml) || heading,
        content: headingHtml,
      });
      setOrigHeadingHtml(headingHtml);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaveError("Failed to save the heading.");
    } finally {
      setSavingText(false);
    }
  };

  const handleSaveImage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageFile) return;
    setSaveError(null);
    setSavingImage(true);
    try {
      await adminApi.uploadPageImage(SLUG, imageFile);
      setImageFile(null);
      load();
    } catch {
      setSaveError("Failed to upload the image.");
    } finally {
      setSavingImage(false);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load the featured offer section.</AlertDescription>
      </Alert>
    );
  }
  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {saveError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Heading</h2>
        <form onSubmit={handleSaveText} className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-gray-500">
              Shown as the bold heading in the featured offer box. Use the toolbar for formatting.
            </label>
            <RichTextEditor
              value={headingHtml}
              onChange={(html) => {
                setHeadingHtml(html);
                setHeading(htmlToPlainText(html));
              }}
              minHeight="180px"
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={savingText || headingHtml === origHeadingHtml}
              className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white"
            >
              {savingText ? (
                <Loader2 size={14} className="animate-spin" />
              ) : saved ? (
                <><Check size={14} className="mr-1" /> Saved</>
              ) : (
                "Save heading"
              )}
            </Button>
          </div>
        </form>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Current image</h2>
        {currentImageUrl ? (
          <div className="max-w-xs mx-auto rounded-md overflow-hidden bg-gray-100 border border-gray-200">
            <img src={currentImageUrl} alt="Featured offer" className="w-full h-auto object-cover" />
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-8 text-center">
            No image uploaded — the offer box shows no image.
          </p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Upload new image</h2>
        <form onSubmit={handleSaveImage} className="space-y-4">
          <ImageDropzone file={imageFile} onChange={setImageFile} />
          <div className="flex justify-end">
            <Button type="submit" disabled={savingImage || !imageFile} className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white">
              {savingImage ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} className="mr-1" /> Save</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
